import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './MicrositeBlogEditor.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeDateTimePicker from '../components/MicrositeDateTimePicker/MicrositeDateTimePicker'
import MicrositeTagInput from '../components/MicrositeTagInput/MicrositeTagInput'
import MicrositeImageUploader from '../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeRichTextEditor from '../components/MicrositeRichTextEditor/MicrositeRichTextEditor'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'scheduled', label: 'Scheduled' }
]

const EMPTY_FORM = {
    title: '',
    slug: '',
    excerpt: '',
    body: '',
    coverImage: '',
    authorName: '',
    tags: [],
    status: 'draft',
    publishAt: '',
    metaTitle: '',
    metaDescription: ''
}

const slugify = (text) =>
    text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-')

const MicrositeBlogEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new' || !id

    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [slugTouched, setSlugTouched] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadExisting = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetBlogPostDetail', { ID: id })
        if (res?.status === 'success') {
            const p = res.response || {}
            const next = {
                title: p.title || '',
                slug: p.slug || '',
                excerpt: p.excerpt || '',
                body: p.body || '',
                coverImage: p.coverImage || '',
                authorName: p.authorName || '',
                tags: p.tags || [],
                status: p.status || 'draft',
                publishAt: p.publishAt ? p.publishAt.replace(' ', 'T').slice(0, 16) : '',
                metaTitle: p.metaTitle || '',
                metaDescription: p.metaDescription || ''
            }
            setForm(next)
            setOriginal(next)
            setSlugTouched(true)
        } else {
            showToast('Could not load post. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, id])

    useEffect(() => {
        if (!isNew && activeStore?.storeIdentifier) loadExisting()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, id])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleTitleChange = (value) => {
        update('title', value)
        if (!slugTouched) update('slug', slugify(value))
    }

    const handleSave = async () => {
        if (!form.title.trim() || !form.slug.trim()) {
            showToast('Title and slug are required', 'error')
            return
        }
        setSaving(true)

        const payload = {
            title: form.title,
            slug: form.slug,
            excerpt: form.excerpt,
            body: form.body,
            coverImage: form.coverImage,
            authorName: form.authorName,
            tags: form.tags,
            status: form.status,
            publishAt: form.publishAt ? form.publishAt.replace('T', ' ') + ':00' : '',
            metaTitle: form.metaTitle,
            metaDescription: form.metaDescription
        }

        const res = isNew
            ? await post('micrositeCreateBlogPost', payload)
            : await post('micrositeUpdateBlogPost', { ID: id, ...payload })

        setSaving(false)

        if (res?.status === 'success') {
            showToast('Post saved successfully')
            setOriginal(form)
            navigate('/microsite/blog')
        } else {
            showToast('Could not save post. Please try again.', 'error')
        }
    }

    const handleDelete = async () => {
        const res = await post('micrositeDeleteBlogPost', { ID: id })
        if (res?.status === 'success') {
            showToast('Post deleted')
            navigate('/microsite/blog')
        } else {
            showToast('Could not delete post', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading post…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={isNew ? 'New blog post' : 'Edit post'}
                backTo="/microsite/blog"
                backLabel="Blog"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onDelete={!isNew ? () => setDeleteConfirmOpen(true) : undefined}
            />

            <section className={styles.fieldGroup}>
                <TextInput
                    label="Title"
                    value={form.title}
                    onChange={handleTitleChange}
                    placeholder="e.g. 5 Hair Care Tips for Monsoon Season"
                    required
                />
                <TextInput
                    label="URL slug"
                    value={form.slug}
                    onChange={(v) => { setSlugTouched(true); update('slug', v) }}
                    placeholder="5-hair-care-tips-monsoon"
                    required
                />
                <TextInput
                    label="Excerpt"
                    value={form.excerpt}
                    onChange={(v) => update('excerpt', v)}
                    placeholder="A short summary shown on the blog listing page"
                    maxLength={200}
                    showCharCount
                />

                <div className={styles.row2}>
                    <TextInput
                        label="Author name"
                        value={form.authorName}
                        onChange={(v) => update('authorName', v)}
                        placeholder="e.g. Priya Sharma"
                    />
                    <SelectInput
                        label="Status"
                        value={form.status}
                        onChange={(v) => update('status', v)}
                        options={STATUS_OPTIONS}
                    />
                </div>

                {form.status === 'scheduled' && (
                    <MicrositeDateTimePicker
                        label="Publish at"
                        value={form.publishAt}
                        onChange={(v) => update('publishAt', v)}
                    />
                )}

                <MicrositeTagInput
                    label="Tags"
                    value={form.tags}
                    onChange={(v) => update('tags', v)}
                />

                <MicrositeImageUploader
                    label="Cover image"
                    value={form.coverImage}
                    onChange={(v) => update('coverImage', v)}
                    placeholder="https://example.com/cover.jpg"
                    aspectHint="Recommended 1200×630px"
                />
            </section>

            <section className={styles.fieldGroup}>
                <MicrositeRichTextEditor
                    label="Post body"
                    value={form.body}
                    onChange={(v) => update('body', v)}
                />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>SEO (optional)</h2>
                <TextInput
                    label="Meta title"
                    value={form.metaTitle}
                    onChange={(v) => update('metaTitle', v)}
                    placeholder="Defaults to post title if left blank"
                />
                <TextInput
                    label="Meta description"
                    value={form.metaDescription}
                    onChange={(v) => update('metaDescription', v)}
                    placeholder="Defaults to excerpt if left blank"
                    maxLength={160}
                    showCharCount
                />
            </section>

            <MicrositeDeleteConfirm
                mode="modal"
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                itemLabel="this blog post"
            />
        </div>
    )
}

export default MicrositeBlogEditor